/* Formulario de una Solicitud a GafCORE (traslado de activos). Que pide cada
 * tipo (equipos, desde, hasta) sale del catalogo de EMSYS; EMSYS y GafCORE
 * vuelven a validar todo al enviar.
 *
 * Mismo patron que el resto de los formularios de la app (AtcNuevaCita,
 * SolicitudRRHHNueva): una IonCard por seccion, IonItem + IonLabel stacked por
 * campo, y los lugares y equipos con el buscador de Solicitudes a RRHH.
 */
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCheckbox,
  IonDatetime,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { closeCircleOutline, warningOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";

import { isoHoyMas } from "src/components/SolicitudCapacitacionCampos";
import SolicitudRRHHBuscador, { OpcionBuscada } from "src/components/SolicitudRRHHBuscador";
import {
  ActivoGafcore,
  CatalogoTraslados,
  LugarGafcore,
  TipoTraslado,
  TrasladosAPI,
} from "src/services/traslados";

const tarjeta: React.CSSProperties = { margin: "0 0 16px" };
// Dentro de IonCardContent el IonItem ya tiene el margen de la tarjeta.
const sinSangria = { "--padding-start": "0" } as React.CSSProperties;

// Un lugar puede tener cientos de equipos: se listan los primeros y se filtra.
const MAX_VISIBLES = 30;

// IonDatetime devuelve un ISO completo; EMSYS espera YYYY-MM-DD y HH:mm.
const soloFecha = (v?: string | null) => (v ? String(v).substring(0, 10) : "");
const soloHora = (v?: string | null) => {
  if (!v) return "";
  const texto = String(v);
  return texto.includes("T") ? texto.substring(11, 16) : texto.substring(0, 5);
};

const avisoActivo = (a: ActivoGafcore) => {
  const notas: string[] = [];
  if (a.traslado_en_curso) notas.push(`ya tiene un traslado en curso${a.traslado_hacia ? ` hacia ${a.traslado_hacia}` : ""}`);
  if (a.componente_de) notas.push(`es parte de ${a.componente_de}`);
  return notas.join("; ");
};

const opcionLugar = (l: LugarGafcore): OpcionBuscada => ({
  id: l.codigo,
  titulo: l.nombre,
  detalle: `${l.codigo}${l.vigente ? "" : " · dado de baja"}`,
});

const opcionActivo = (a: ActivoGafcore): OpcionBuscada => ({
  id: a.codigo_g,
  titulo: `${a.codigo_g}${a.cod_fracttal ? ` / ${a.cod_fracttal}` : ""} · ${a.nombre}`,
  detalle: a.ubicacion_nombre ? `En ${a.ubicacion_nombre}` : "Sin ubicación registrada",
});

/** Buscador de lugares de GafCORE (nuestros PS, por nombre o código objeto). */
const BuscadorLugar: React.FC<{
  valor: LugarGafcore | null;
  onElegir: (l: LugarGafcore | null) => void;
  soloVigentes?: boolean;
  onError: (m: string) => void;
}> = ({ valor, onElegir, soloVigentes, onError }) => {
  const [resultados, setResultados] = useState<LugarGafcore[]>([]);
  const lista = valor && !resultados.some((r) => r.codigo === valor.codigo) ? [valor, ...resultados] : resultados;
  return (
    <SolicitudRRHHBuscador
      opciones={lista.map(opcionLugar)}
      valor={valor?.codigo || ""}
      onElegir={(id) => onElegir(lista.find((l) => l.codigo === id) || null)}
      placeholder="Buscar PS por nombre o código"
      onBuscar={(q) =>
        TrasladosAPI.lugares(q, soloVigentes)
          .then((r) => setResultados(r.data.data || []))
          .catch((err) => onError(err?.response?.data?.error || "No se pudieron buscar los lugares"))
      }
    />
  );
};

interface Props {
  catalogo: CatalogoTraslados;
  /** Devuelve true si salió bien (la pantalla cambia de pestaña). */
  onEnviar: (payload: Record<string, any>) => Promise<boolean>;
  onError: (mensaje: string) => void;
}

const SolicitudGafcoreFormulario: React.FC<Props> = ({ catalogo, onEnviar, onError }) => {
  const [tipo, setTipo] = useState<TipoTraslado | null>(null);
  const [origen, setOrigen] = useState<LugarGafcore | null>(null);
  const [destino, setDestino] = useState<LugarGafcore | null>(null);
  const [enLugar, setEnLugar] = useState<ActivoGafcore[]>([]);
  const [cargandoLugar, setCargandoLugar] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [elegidos, setElegidos] = useState<ActivoGafcore[]>([]);
  const [encontrados, setEncontrados] = useState<ActivoGafcore[]>([]);
  // Cambia al sumar un equipo por el buscador: lo vuelve a montar vacío.
  const [vueltaBuscador, setVueltaBuscador] = useState(0);
  const [fecha, setFecha] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [detalle, setDetalle] = useState("");
  const [enviando, setEnviando] = useState(false);

  const llevaEquipos = tipo?.equipos === "si";

  // Los equipos del lugar de origen, para marcarlos.
  useEffect(() => {
    setEnLugar([]);
    setFiltro("");
    if (!llevaEquipos || !origen) return;
    let cancelado = false;
    setCargandoLugar(true);
    TrasladosAPI.activos({ ubicacion: origen.codigo })
      .then((r) => !cancelado && setEnLugar(r.data.data.activos || []))
      .catch((err) => !cancelado && onError(err?.response?.data?.error || "No se pudieron traer los equipos del lugar"))
      .finally(() => !cancelado && setCargandoLugar(false));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origen, llevaEquipos]);

  const filtrados = useMemo(() => {
    const f = filtro.trim().toUpperCase();
    return enLugar.filter((a) => !f || `${a.codigo_g} ${a.cod_fracttal || ""} ${a.nombre}`.toUpperCase().includes(f));
  }, [enLugar, filtro]);

  const estaElegido = (a: ActivoGafcore) => elegidos.some((e) => e.codigo_g === a.codigo_g);

  const alternar = (a: ActivoGafcore) => {
    if (estaElegido(a)) {
      setElegidos(elegidos.filter((e) => e.codigo_g !== a.codigo_g));
    } else if (elegidos.length >= catalogo.max_equipos) {
      onError(`Se pueden pedir hasta ${catalogo.max_equipos} equipos por solicitud.`);
    } else {
      setElegidos([...elegidos, a]);
    }
  };

  const enviar = async () => {
    if (!tipo) return onError("Seleccioná el tipo de solicitud.");
    const equipos = llevaEquipos ? elegidos.map((a) => a.codigo_g) : [];
    if (llevaEquipos && !equipos.length) return onError("Seleccioná al menos un equipo.");
    if (tipo.desde !== "no" && !origen && (tipo.desde === "si" || equipos.length > 1))
      return onError("Indicá desde qué lugar salen los equipos.");
    if (tipo.hasta === "si" && !destino) return onError("Indicá hasta qué lugar van.");
    if (!!desde !== !!hasta) return onError("Indicá el horario completo (desde y hasta) o dejalo vacío.");
    if (desde && !fecha) return onError("El horario necesita una fecha.");
    if (desde && desde >= hasta) return onError('El horario "desde" tiene que ser anterior al "hasta".');
    if (!detalle.trim()) return onError("Describí qué se necesita en el detalle.");

    setEnviando(true);
    const ok = await onEnviar({
      tipo: tipo.valor,
      origen: tipo.desde !== "no" ? origen?.codigo || "" : "",
      destino: destino?.codigo || "",
      equipos,
      fecha_requerida: fecha,
      horario_desde: desde,
      horario_hasta: hasta,
      detalle: detalle.trim(),
    });
    if (!ok) setEnviando(false);
  };

  const ayudaDesde = !tipo
    ? ""
    : tipo.valor === "RETIRO_DE_PUNTO_DE_SERVICIO"
    ? "El lugar que se cierra: GAF retira todos sus equipos."
    : llevaEquipos
    ? "Obligatorio si pedís más de un equipo."
    : "";

  return (
    <>
      <IonCard style={tarjeta}>
        <IonCardHeader>
          <IonCardSubtitle>¿Qué necesitás?</IonCardSubtitle>
        </IonCardHeader>
        <IonList lines="full">
          <IonItem lines="none">
            <IonLabel position="stacked">Tipo de solicitud *</IonLabel>
            <IonSelect
              interface="action-sheet"
              interfaceOptions={{ header: "Tipo de solicitud" }}
              cancelText="Cancelar"
              placeholder="Seleccionar"
              value={tipo?.valor}
              onIonChange={(e) => setTipo(catalogo.tipos.find((t) => t.valor === e.detail.value) || null)}
              disabled={enviando}
            >
              {catalogo.tipos.map((t) => (
                <IonSelectOption key={t.valor} value={t.valor}>
                  {t.etiqueta}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        </IonList>
      </IonCard>

      {tipo && (
        <>
          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>Desde dónde y hasta dónde</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent style={{ paddingTop: 0 }}>
              {tipo.desde !== "no" && (
                <div style={{ marginBottom: 12 }}>
                  <IonItem lines="none" style={sinSangria}>
                    <IonLabel className="ion-text-wrap">
                      <h2 style={{ fontWeight: 600 }}>
                        Desde{tipo.desde === "si" ? " *" : tipo.desde === "opc" && !llevaEquipos ? " (opcional)" : ""}
                      </h2>
                      {ayudaDesde && <IonNote color="medium">{ayudaDesde}</IonNote>}
                    </IonLabel>
                  </IonItem>
                  <BuscadorLugar valor={origen} onElegir={setOrigen} onError={onError} />
                </div>
              )}
              <IonItem lines="none" style={sinSangria}>
                <IonLabel>
                  <h2 style={{ fontWeight: 600 }}>Hasta{tipo.hasta === "si" ? " *" : " (opcional)"}</h2>
                </IonLabel>
              </IonItem>
              {/* A un lugar dado de baja no se mandan equipos. */}
              <BuscadorLugar valor={destino} onElegir={setDestino} soloVigentes onError={onError} />
            </IonCardContent>
          </IonCard>

          {llevaEquipos && (
            <IonCard style={tarjeta}>
              <IonCardHeader>
                <IonCardSubtitle>
                  Equipos * ({elegidos.length} de {catalogo.max_equipos})
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent style={{ paddingTop: 0 }}>
                {elegidos.length > 0 && (
                  <IonList lines="full" style={{ marginBottom: 12 }}>
                    {elegidos.map((a) => (
                      <IonItem key={a.codigo_g} style={sinSangria}>
                        <IonLabel className="ion-text-wrap">
                          <h3>
                            {a.codigo_g}
                            {a.cod_fracttal ? ` / ${a.cod_fracttal}` : ""}
                          </h3>
                          <p>{a.nombre}</p>
                        </IonLabel>
                        <IonButton fill="clear" color="danger" slot="end" disabled={enviando} onClick={() => alternar(a)}>
                          <IonIcon icon={closeCircleOutline} slot="icon-only" />
                        </IonButton>
                      </IonItem>
                    ))}
                  </IonList>
                )}

                {!origen ? (
                  <IonText color="medium">
                    <p style={{ margin: "0 0 8px" }}>Elegí el lugar de origen para ver sus equipos, o buscalos abajo.</p>
                  </IonText>
                ) : (
                  <>
                    <IonText color="medium">
                      <p style={{ margin: 0 }}>Equipos en {origen.nombre}: tocá para marcarlos.</p>
                    </IonText>
                    <IonSearchbar
                      value={filtro}
                      placeholder="Filtrar por código o nombre"
                      onIonInput={(e) => setFiltro((e.target as HTMLIonSearchbarElement).value || "")}
                      onIonClear={() => setFiltro("")}
                      style={{ paddingInline: 0 }}
                      disabled={enviando || cargandoLugar}
                    />
                    {cargandoLugar ? (
                      <div style={{ textAlign: "center", padding: 16 }}>
                        <IonSpinner />
                      </div>
                    ) : !filtrados.length ? (
                      <IonText color="medium">
                        <p style={{ margin: "0 0 8px" }}>
                          {enLugar.length ? "Ningún equipo coincide con el filtro." : "GafCORE no registra equipos en este lugar."}
                        </p>
                      </IonText>
                    ) : (
                      <IonList lines="full">
                        {filtrados.slice(0, MAX_VISIBLES).map((a) => (
                          <IonItem
                            key={a.codigo_g}
                            button
                            detail={false}
                            disabled={enviando}
                            onClick={() => alternar(a)}
                            style={sinSangria}
                          >
                            <IonCheckbox slot="start" checked={estaElegido(a)} style={{ pointerEvents: "none" }} />
                            <IonLabel className="ion-text-wrap">
                              <h3>
                                {a.codigo_g}
                                {a.cod_fracttal ? ` / ${a.cod_fracttal}` : ""}
                              </h3>
                              <p>{a.nombre}</p>
                              {avisoActivo(a) && (
                                <IonNote color="warning" style={{ fontSize: 12 }}>
                                  <IonIcon icon={warningOutline} /> {avisoActivo(a)}
                                </IonNote>
                              )}
                            </IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    )}
                    {filtrados.length > MAX_VISIBLES && (
                      <IonText color="medium">
                        <p style={{ margin: "4px 0 8px" }}>
                          Se muestran {MAX_VISIBLES} de {filtrados.length}. Filtrá para encontrar el resto.
                        </p>
                      </IonText>
                    )}
                  </>
                )}

                <IonItem lines="none" style={{ ...sinSangria, marginTop: 8 }}>
                  <IonLabel className="ion-text-wrap">
                    <h2 style={{ fontWeight: 600 }}>{origen ? "¿No está en la lista?" : "Buscar equipo"}</h2>
                    <IonNote color="medium">
                      Si el sistema lo tiene en otro lugar, el pedido entra igual y GAF ve la diferencia.
                    </IonNote>
                  </IonLabel>
                </IonItem>
                <SolicitudRRHHBuscador
                  key={vueltaBuscador}
                  opciones={encontrados.filter((a) => !estaElegido(a)).map(opcionActivo)}
                  valor=""
                  onElegir={(id) => {
                    const a = encontrados.find((x) => x.codigo_g === id);
                    if (a) alternar(a);
                    setEncontrados([]);
                    setVueltaBuscador((n) => n + 1);
                  }}
                  placeholder="Código G, código Fracttal o nombre"
                  minimo={3}
                  onBuscar={(q) =>
                    TrasladosAPI.activos({ q })
                      .then((r) => setEncontrados(r.data.data.activos || []))
                      .catch((err) => onError(err?.response?.data?.error || "No se pudieron buscar los equipos"))
                  }
                />
              </IonCardContent>
            </IonCard>
          )}

          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>Cuándo (opcional)</IonCardSubtitle>
            </IonCardHeader>
            <IonList lines="full">
              <IonItem>
                <IonLabel position="stacked">Para el día</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  placeholder="Seleccionar la fecha"
                  min={catalogo.primera_fecha}
                  max={isoHoyMas(730)}
                  value={fecha || undefined}
                  onIonChange={(e) => setFecha(soloFecha(e.detail.value))}
                  disabled={enviando}
                />
                <IonNote slot="helper" color="medium">
                  Con al menos 3 días de antelación.
                </IonNote>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Horario desde</IonLabel>
                <IonDatetime
                  displayFormat="HH:mm"
                  pickerFormat="HH:mm"
                  cancelText="Cancelar"
                  doneText="Listo"
                  placeholder="hh:mm"
                  value={desde || undefined}
                  onIonChange={(e) => setDesde(soloHora(e.detail.value))}
                  disabled={enviando}
                />
              </IonItem>
              <IonItem lines="none">
                <IonLabel position="stacked">Horario hasta</IonLabel>
                <IonDatetime
                  displayFormat="HH:mm"
                  pickerFormat="HH:mm"
                  cancelText="Cancelar"
                  doneText="Listo"
                  placeholder="hh:mm"
                  value={hasta || undefined}
                  onIonChange={(e) => setHasta(soloHora(e.detail.value))}
                  disabled={enviando}
                />
              </IonItem>
            </IonList>
          </IonCard>

          <IonCard style={tarjeta}>
            <IonCardHeader>
              <IonCardSubtitle>Detalle</IonCardSubtitle>
            </IonCardHeader>
            <IonList lines="full">
              <IonItem lines="none">
                <IonLabel position="stacked">¿Qué se necesita? *</IonLabel>
                <IonTextarea
                  value={detalle}
                  placeholder="Contalo en palabras: es lo que lee quien lo despacha."
                  onIonChange={(e) => setDetalle(e.detail.value || "")}
                  autoGrow
                  rows={3}
                  maxlength={4000}
                  disabled={enviando}
                />
              </IonItem>
            </IonList>
          </IonCard>

          <IonButton expand="block" disabled={enviando} onClick={enviar}>
            {enviando ? <IonSpinner name="crescent" /> : "Enviar a GafCORE"}
          </IonButton>
        </>
      )}
    </>
  );
};

export default SolicitudGafcoreFormulario;
